// StationCharacteristicsPopup.js


import { useState, useEffect, useRef, useContext } from "react";
import { useFetch } from "use-http";
import { Form, Box, Text, Button, Layer, Header, Image, Spinner, Select, TextInput } from "grommet";
import { getDBURL, updateStateHandler, isValidYearString, correctTimeZone, usePopupFocus } from "../Utils/Utils";
import useTrapFocus from "../Utils/useTrapFocus";
import { UserContext } from "../../App";
import { useDataContext } from '../Contexts/DataContext';
import VineCharacteristics from  "./VineCharacteristics";
import DangerPopupWindow from "./DangerPopupWindow";
import Notification from "./Notification";

import {
    getTypeOptions,
    getBudBreakOptions,
    getRainResistanceOptions,
    getChillingOptions,
} from "../Utils/RBGUtils";


const StationCharacteristicsPopup = (props) => {
    const { station, loadActiveStations, setSelectedStation} = props;
    console.log('StationCharacteristicsPopup props  >>  ', props);

    const currentUser = useContext(UserContext);
    const { rootstockList } = useDataContext();
    const [loading, setLoading] = useState(true);    
    const { get, post } = useFetch(getDBURL());

    // const [rootstock, setRootstock] = useState({Id: 0, Name: ""}); // the selected rootstock object to display in the combo box
    // const [open, setOpen] = useState(false); // show/hide 'Confirm' popup 
    // const [warningMessage, setWarningMessage] = useState('');

    const [success, setSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");    
    const [isButtonClicked, setIsButtonClicked] = useState(false);

    const [originalStationData, setOriginalStationData] = useState(null);
    const [originalCultivarData, setOriginalCultivarData] = useState(null);

    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open   // const [showDangerPopup, setShowDangerPopup] = useState(false);

    // console.log('loading >> ', loading);

    const setStationValues = (values) => {
        return {
            StationID: values.Id,  
            StationName: values.StationName,   
            PrevStationId: values.PrevStationId,
            SeedlingId: values.SeedlingId,
            CultivarId: values.CultivarId,
            RootstockId: values.RootstockId,
            RootstockName: values.RootstockName,
            PlantingYear: values.PlantingYear,
            BestTreatmentId: values.BestTreatmentId,
            BestTreatmentDate: values.BestTreatmentDate,
            BestTreatmentBy: values.BestTreatmentBy,
            Comment: values.Comment,
            LastSyncDate: values.LastSyncDate,   
        };
    };
    const [stationData, setStationData] = useState({});

    const setCultivarValues = (values) => {
        return {
            CultivarID: values.Id,  
            CultivarName: values.CultivarName,     
            Brand: values.Brand,
            Mother: values.Mother,
            Father: values.Father,
            // RootstockId: values.RootstockId,
            Type: values.Type, 
            Harvest: values.Harvest, 
            BudBreak: values.BudBreak, 
            RainResistance: values.RainResistance, 
            Chilling: values.Chilling,
            IsCommercial: values.IsCommercial,
            CommercialFromDate: values.CommercialFromDate,
            SetAsCommercialBy: values.SetAsCommercialBy,
            AddDate: values.AddDate,
            AddedBy: values.AddedBy,
            Country: values.Country,
            Company: values.Company,
            Notes: values.Notes,            
            LastSyncDate: values.LastSyncDate,   
        };
    };

    const [cultivarData, setCultivarData] = useState({});


    const loadData = async () => {
        try {
            const stationResponse = await get("/loadStation/stationId=" + station.Id + "/dt=" + Date.now());
            if (!stationResponse.success) throw new Error(`Failed to load station data: ${stationResponse.message}`);
    
            const cultivarResponse = await get("/loadCultivar/cultivarId=" + station.CultivarId + "/dt=" + Date.now());
            if (!cultivarResponse.success) throw new Error(`Failed to load cultivar data: ${cultivarResponse.message}`);

            const stationVal = setStationValues(stationResponse.result[0]);
            const cultivarVal = setCultivarValues(cultivarResponse.result[0]);

            setStationData(stationVal);
            setCultivarData(cultivarVal);
            setOriginalStationData(stationVal);
            setOriginalCultivarData(cultivarVal);
    
            setLoading(false);
        } catch (error) {
            console.error(error);
            setShowError(true);
            setNotificationMessage(error.message || 'An error occurred!');
        }
    };
    
    useEffect(() => {
        loadData();
    }, []);
    
    const [dangerPopupOpen, setDangerPopupOpen] = useState(false); // Danger Popup Window
    const [dangerFuncOnConfirm, setDangerFuncOnConfirm] = useState('');

    usePopupFocus(dangerPopupOpen, "danger-msg");
    
    // opens a popup window when the delete" button is clicked
    const openDangerPopupWindow = ( func) => {
        setDangerFuncOnConfirm(() => func); // Store the callback function without executing it
        setDangerPopupOpen(true);
    }

    // hides the "Danger" popup window 
    const onDangerPopupClose = () => {
        setDangerPopupOpen(false);    
    };

    // fires when the "Yes" button is clicked in the "Danger" popup
    const onDangerConfirm = () => {
        // console.log(' << in function onDangerConfirm >> '); 
        if (typeof dangerFuncOnConfirm === 'function') {
            dangerFuncOnConfirm();
        } 
        onDangerPopupClose();
        setDangerFuncOnConfirm('');
    };

    const handleRootstockChange = (option) => {
        if (stationData.RootstockName) {
            // If there's a previous value, open the danger popup
            openDangerPopupWindow(() => onRootstChange(option));
        } else {
            // If the previous value is empty, directly run the function
            onRootstChange(option);
        }
    };

    const onRootstChange = ( newSelectedRS ) => {
        // console.log('option >> ', newSelectedRS);
        // console.log('prev option >> ', rootstock);
        const selectedRS = ( newSelectedRS === undefined) 
        ? {  
            Id: '00000000-0000-0000-0000-000000000000',
            Name: ''
        }
        : {  
            Id: newSelectedRS.Id,
            Name: newSelectedRS.Name
        }
        updateStateHandler( setStationData, 'RootstockId', selectedRS.Id);
        updateStateHandler( setStationData, 'RootstockName', selectedRS.Name);
     }; 

    const onYearChange = (plantingYear) => {
        const plantingYearIsValid = isValidYearString(plantingYear, true, 'Planting Year');
        if (plantingYearIsValid.res) {        
            updateStateHandler( setStationData, 'PlantingYear', Number(plantingYear));
        }
    };  


    const onStationNameChange = (newName) => {
        const trimmedName = newName.trim();
        updateStateHandler(setStationData, 'StationName', trimmedName);
    };

    const isValidStationNameChange = (newName) => {
        const trimmedName = newName.trim();
        if (!trimmedName) {
            setShowError(true);
            setNotificationMessage('Station name is required. Please enter a valid name before saving.');                
            return false;
       }
       return true;
    };

    const hasChanges = () => {
        const isStationEqual = JSON.stringify(stationData) === JSON.stringify(originalStationData);
        const isCultivarEqual = JSON.stringify(cultivarData) === JSON.stringify(originalCultivarData);
        return !(isStationEqual && isCultivarEqual);
    };
    
    const submitHandler = async(event) => {
        if (!hasChanges()) {
            // Optionally show a toast saying "No changes to save"
            props.onClose();
            return;
        }

        if (!isValidStationNameChange(stationData.StationName)) {
            return;
        }

        updateStateHandler( setStationData, 'LastSyncDate', correctTimeZone(new Date()));
        updateStateHandler( setCultivarData, 'LastSyncDate', correctTimeZone(new Date()));

        const timestamp = Date.now();
        // console.log('cultivarData: ', cultivarData);
        // console.log('In submitHandler stationData: ', stationData);
        try {
            // Perform both post requests simultaneously
            const [udateStationResponse, updateCultivarResponse] = await Promise.all([
                post(`/updateStation/dt=${timestamp}`,stationData),
                post(`/updateCultivar/dt=${timestamp}`,cultivarData)
            ]);

            // Check if the response is successful
            if (!udateStationResponse.success || !updateCultivarResponse.success) {
                throw new Error(`HTTP error! ${udateStationResponse.message}`);
            }
            const result = udateStationResponse.result + updateCultivarResponse.result;

            // Log the results (1 for success, 0 for failure)
            // console.log('Update Station result: ', result);

            if (result === 2) {
                // After both updates are successful, update state and trigger callbacks
                setIsButtonClicked(true);
                loadActiveStations(); 
                setSelectedStation({ 
                    Id: stationData.StationID, 
                    Name: stationData.StationName,
                    DisplayName: stationData.StationName,
                    Rootstock: stationData.RootstockId === null ? station.Rootstock : stationData.RootstockName,
                    CultivarId: station.CultivarId,
                    FruitColor: cultivarData.Type,
                    Year: stationData.PlantingYear, 
                    Mother: station.Mother,
                    Father: station.Father,
                    SeedlingRow: station.SeedlingRow,
                    SeedlingColumn: station.SeedlingColumn});
                setSuccess(true);
                setNotificationMessage("Station data was updated successfully.");

                // Delay closing the modal to let the user see the message
                setTimeout(() => {
                    setSuccess(false); // optionally close the notification manually
                    props.onClose();
                }, 2000); // 2 seconds
            }
        } catch (error) {
            // Handle errors if any
            console.error('Error updating data:', error);
            // You might want to handle errors differently, e.g., show an error message to the user
            setShowError(true);
            setNotificationMessage('An error occurred while updating station data.');                

        }
    };


    return (
            <Layer
                id="stat-char"
                ref={modalRef}
                tabIndex="-1"                
                position="center"
                modal
                style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dim background for modal effect
                }}
            >
                <Box 
                    overflow='auto'
                    pad="small" 
                    gap="small" 
                    //width="medium" 
                    width="large" 
                    background="light-3"  
                    border={{ color: "brand"}}
                    style={{
                        position: 'relative',
                        margin: 'auto', // Center horizontally and vertically
                    }}
                >

                <Form direction="row" justify="between" align="start" > 
                {showError && (
                    <Notification
                    status="critical"
                    message={`${notificationMessage}`}
                    onClose={() => setShowError(false)}
                    />
                )}
                {success && (
                    <Notification
                    status="normal"
                    message={`${notificationMessage}`}
                    onClose={() => setSuccess(false)}
                    />
                )}  
                    {loading ? (
                        <Box align="center" direction="row" gap="small" pad="small">
                            <Spinner /> <Text>Loading...</Text>
                        </Box>
                    ) : ( 
                        <>
                            <Box pad="small" margin="auto" gap="small" width="large"  border >

                                <Box style={{position:"relative"}} >
                                    <Header  margin="none" background="light-4" pad="small" height="xxsmall" justify="around" >
                                        <Box  direction="row" align="end">
                                            <Box  align="centernd" >
                                                <Text color="brand" size="large" alignSelf="center" >
                                                    <strong>Station &nbsp; 
                                                </strong>
                                                </Text>
                                            </Box>
                                            <Box direction="row"  width="200px" >
                                                <TextInput                    
                                                    value={stationData.StationName} 
                                                    style={{whiteSpace: 'pre-line', fontWeight: "bold", fontSize: "26px"
                                                            , color:'#FFAA15', padding: "0 5px", border: "none"
                                                        , background: "#E2E2E2"}}                                             
                                                    onChange={(event) => {
                                                        onStationNameChange(event.target.value);
                                                    }}
                                                />
                                            </Box>                                          
                                        </Box> 

                                    </Header>
                                    <Box height="xsmall" width="xsmall" 
                                        style={{position:"absolute", right: "3%", top: "15px"}}
                                        right="0px"
                                        >
                                            <Image
                                                size="contain"
                                                src="Grape.png"
                                                alt="Grape Icon"                              
                                            />
                                    </Box>                 
                                </Box>

                                {!isNaN(station.SeedlingRow) && !isNaN(station.SeedlingColumn) &&
                                <Box direction="row-responsive" >
                                    <Box align="start"  width="50%">                                    
                                        <Text  margin="small" textAlign="center"  size="medium" >
                                            <strong>From seedling&nbsp;&nbsp;&nbsp;&nbsp;
                                            <span style={{whiteSpace: 'pre-line', fontWeight: "bold", fontSize: "18px", color:'#FFAA15'}}>
                                                {isNaN(station.SeedlingRow) || isNaN(station.SeedlingColumn) ? 
                                                '' :
                                                `${station.SeedlingRow} - ${station.SeedlingColumn}`}
                                            </span></strong>        
                                        </Text> 
                                    </Box>

                                    <Box direction="row" align="center"  width="50%" >                                    
                                        <Text  margin="small" textAlign="center"  size="medium" >
                                        <strong>Planting year &nbsp;</strong></Text>
                                        <Box direction="row"  width="80px" >
                                            <TextInput
                                                type="number"                        
                                                value={stationData.PlantingYear}
                                                style={{padding: "0 5px", border: "none", background: "#f7f7f7", fontWeight: "bold", color:'#FFAA15'}} 
                                                
                                                onChange={(event) => {
                                                    onYearChange(event.target.value);
                                                }}
                                            />
                                        </Box>  
                                    </Box>                                    
                                </Box>
                                }

                                <Box direction="row-responsive" alignSelf="center" width="100%" >

                                    {/* Rootstock Select */}
                                    <Box direction="row" width="50%" align="center">
                                        <Text  margin="small" size="medium"><strong>Rootstock:&nbsp;&nbsp;</strong></Text>
                                        {/* {cultivarData.CreateDate !== null ? (
                                            <Text size="medium" color='#FFAA15'>
                                            <strong>{cultivarData.RootstockName === null ? '' : cultivarData.RootstockName}</strong>
                                            </Text>
                                        ) : ( */}
                                            <Select
                                            value={stationData.RootstockName}
                                            valueKey={{key: "Name", reduce: true }}
                                            labelKey="Name"                                            
                                            placeholder="Select Rootstock..."
                                            style={{padding: "1px 0 1px 10px", margin: "0", width: "200px"}}
                                            options={rootstockList} 
                                            // onChange={({option}) => openDangerPopupWindow( () =>  onRootstChange(option))}
                                            onChange={({ option }) => handleRootstockChange(option)}                  
                                            emptySearchMessage={"No Rootstock Available"} 
                                            // clear                                  
                                            />
                                        {/* )} */}
                                    </Box>
                                    <Box direction="row" width="50%" align="center">
                                    </Box>
                                </Box>

                                {dangerPopupOpen && (
                                <DangerPopupWindow
                                    msg={
                                        "You're about to replace the rootstock with a new one! Please confirm if this is the correct action before proceeding!"
                                    }
                                    onClose={onDangerPopupClose}
                                    onConfirm={onDangerConfirm}
                                />
                                )} 


                                <Box direction="row-responsive" >
                                    <Box direction="row" align="start"  width="50%" >                                    
                                        <Text  margin="small" textAlign="center"  size="medium" >
                                            <strong>Mother &nbsp; 
                                                <span style={{whiteSpace: 'pre-line', fontWeight: "bold", fontSize: "18px", color:'#FFAA15'}}>{station.Mother}
                                                </span></strong>
                                        </Text>
                                    </Box>

                                    <Box direction="row" align="start"  width="50%" >                                    
                                        <Text  margin="small" textAlign="center"  size="medium" >
                                        <strong>Father &nbsp;
                                        <span style={{whiteSpace: 'pre-line', fontWeight: "bold", fontSize: "18px", color:'#FFAA15'}}>{station.Father}
                                            </span></strong></Text>
                                    </Box>
                                </Box>

                                <Box alignSelf="center"  width="90%">
                                    <VineCharacteristics cultivarData={cultivarData} setCultivarData={setCultivarData} readOnly={cultivarData.IsCommercial === 1 ? true : false}/>
                                </Box> 

                                <Box 
                                    as="footer"
                                    gap="small"
                                    direction="row"
                                    align="center"
                                    justify="around"
                                    pad={{ top: 'xxsmall', bottom: 'medium' }}
                                >
                                    <Box align="center" direction="row" gap="small" pad="small" justify="around">
                                        <Button primary label="Save changes" onClick={submitHandler} style={{paddingTop: '2px', paddingBottom: '2px', fontSize: '16px', color: 'white'}} disabled={isButtonClicked} />
                                        <Button secondary label="Cancel" onClick={() => {props.onClose(false)}} style={{ paddingTop: '2px', paddingBottom: '2px', fontSize: '16px'}} />
                                    </Box>
                                </Box>                                
                            </Box>

                        </>)} 
                </Form> 
                </Box>   
            </Layer>
        
    );
}

export default StationCharacteristicsPopup;
